import axios from "axios";
import { APIURL } from "../Common/APIURL";
import Constant from "../Common/Constant";
import Toast from "../Common/Toastify";
import Swal from "sweetalert2";

const api = axios.create({
  baseURL: APIURL,
  withCredentials: true,
});

const isNetworkError = (error) => {
  return (
    error?.isOffline === true ||
    !navigator.onLine ||
    error?.message === "Network Error" ||
    error?.code === "ERR_NETWORK" ||
    (!error?.response &&
      error?.code !== "ECONNABORTED" &&
      !error?.isOffline === false)
  );
};

// ─── Offline short-circuit ──────────────────────────────────────────────────
api.interceptors.request.use((config) => {
  if (!navigator.onLine) {
    return Promise.reject({ isOffline: true, config });
  }
  return config;
});
// ──────────────────────────────────────────────────────────────────────────

export { api };

// ─── Refresh Token Interceptor ─────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) =>
    error ? prom.reject(error) : prom.resolve(token),
  );
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isNetworkError(error)) {
      return Promise.reject({ ...error, isOffline: true });
    }
     if (
      error.response?.status === 401 &&
      error.response?.data?.code === "ACCOUNT_DEACTIVATED"
    ) {
      localStorage.clear();
      window.location.href = "/login?reason=deactivated";
      return Promise.reject(error);
    }
    const originalRequest = error.config;

    const isAuthExemptEndpoint =
      originalRequest.url.includes("Auth/RefreshToken") ||
      originalRequest.url.includes("Auth/SSOExchangeCode") ||
      originalRequest.url.includes("Auth/Login") ||
      originalRequest.url.includes("Auth/SSOLogin");

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url.includes("Auth/RefreshToken")) {
        localStorage.removeItem("token");
        if (!window.location.pathname.includes("/login")) {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
      if (isAuthExemptEndpoint) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const res = await api.post("Auth/RefreshToken");
        const newToken = res.data.token;

        localStorage.setItem("token", newToken);
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem("token");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);
// ──────────────────────────────────────────────────────────────────────────────

export const apiRequest = async (
  method,
  url,
  data = null,
  forSave = false,
  forActive = false,
  forDelete = false,
  customErrorMessage = false,
  forUpdate = false,
  headers = {},
  isDownload = false, // New parameter to handle file download
  isPIFConfig = false,
  isPreview = false,
  isDeleteSwalNeed = true,
) => {
  const confirmDialog = async () => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      backdrop: true, // Adds Windows-style overlay
    });

    return result.isConfirmed; // True if "Yes" clicked
  };

  try {
    if (forDelete && isDeleteSwalNeed) {
      const userConfirmed = await confirmDialog();
      if (!userConfirmed) return null;
    }
    const token = localStorage.getItem("token");
    if (data instanceof FormData) {
      headers["Content-Type"] = "multipart/form-data";
    } else {
      headers["Content-Type"] = "application/json";
    }
    if (token) {
      headers = { ...headers, Authorization: `Bearer ${token}` };
    }

    const response = await api({
      method,
      url,
      data,
      headers,
      responseType: isDownload ? "arraybuffer" : isPreview ? "blob" : "json",
    });

    if (forSave || forDelete || forActive || forUpdate) {
      const ResponseData = response?.data?.Result
        ? response?.data?.Result
        : response?.data;

      if (ResponseData != "") {
        const isNumber = !isNaN(Number(ResponseData));

        if (isNumber && Number(ResponseData) > 0) {
          Toast(
            forSave
              ? Constant.successFullySaved
              : forUpdate
                ? Constant.updateStatus
                : forActive
                  ? Constant.successActive
                  : forDelete
                    ? Constant.successfullyDeleted
                    : undefined,
            "success",
          );

          if (response?.data?.Message) {
            Toast(
              response?.data?.Message,
              response?.data?.type ? response?.data?.type : "success",
            );
          }
        } else {
          if (!customErrorMessage && ResponseData != -1 && !isPIFConfig) {
            Toast(
              forSave
                ? Constant?.failToSave
                : forUpdate
                  ? Constant.failedUpdateStatus
                  : forActive
                    ? Constant.failedToActive
                    : forDelete
                      ? Constant.failToDelete
                      : undefined,
              "error",
            );
          } else if (ResponseData == -1) {
            Toast(forSave ? "Data Already Exists" : "", "error");
          } else if (isPIFConfig) {
            if (ResponseData == -2) {
              Toast(
                forSave ? "Duplicate Field Label found in the list" : "",
                "warning",
              );
            } else if (ResponseData == -3) {
              Toast("Duplicate option value found in the list.", "warning");
            }
          }
        }
      } else {
        if (!customErrorMessage) {
          Toast(
            forSave
              ? Constant.failToSave
              : forUpdate
                ? Constant.failedUpdateStatus
                : forActive
                  ? Constant.failedToActive
                  : forDelete
                    ? Constant.failToDelete
                    : undefined,
            "error",
          );
        }
      }
    }

    return {
      success:
        Array.isArray(response.data) ||
        (typeof response.data === "object" && response.data !== null) ||
        !isNaN(Number(response.data) || Boolean(String(response?.data))),
      data: response?.data?.Result ? response?.data?.Result : response?.data,
    };
  } catch (error) {
    if (isNetworkError(error)) {
      Toast("No internet connection. Please check your network.", "error");
      return {
        success: false,
        isOffline: true,
        error: "No internet connection",
      };
    }
    if (!customErrorMessage) {
      Toast(
        forSave
          ? Constant.failToSave
          : forUpdate
            ? Constant.failedUpdateStatus
            : forActive
              ? Constant.failedToActive
              : forDelete
                ? Constant.failToDelete
                : undefined,
        "error",
      );
    }
    console.error("API Error:", error);
    return {
      success: false,
      error:
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.message,
      data: error.response?.data,
    };
  }
};

export default apiRequest;
